/**
 * Enumerations from AGROFARM_DEVELOPMENT_PLAN.md §17.4 and §17.5.
 *
 * Defined as const objects rather than TypeScript `enum` so the values are plain
 * strings at runtime — they cross the API, land in Postgres and appear in Zod
 * schemas unchanged, with no reverse-mapping surprises.
 */

/** §17.4 — every kind of money movement the ledger records. */
export const TRANSACTION_TYPE = {
  EXPENSE: 'expense',
  INCOME: 'income',
  CAPITAL_IN: 'capital_in',
  DRAWING: 'drawing',
  TRANSFER_OUT: 'transfer_out',
  TRANSFER_IN: 'transfer_in',
  PAYABLE_PAYMENT: 'payable_payment',
  RECEIVABLE_COLLECTION: 'receivable_collection',
  ASSET_PURCHASE: 'asset_purchase',
  DEPRECIATION: 'depreciation',
  LOAN_IN: 'loan_in',
  LOAN_PAYMENT: 'loan_payment',
  ADVANCE_LIABILITY: 'advance_liability',
  ADVANCE_SETTLEMENT: 'advance_settlement',
  /**
   * P4-BR1 — a cancelled booking returning a customer's deposit.
   *
   * Distinct from `expense` on purpose: returned money was never the farm's,
   * and filing it as spending would inflate costs in the one month of the
   * year when the Eid margin is being judged (D-015).
   */
  ADVANCE_REFUND: 'advance_refund',
} as const;
export type TransactionType = (typeof TRANSACTION_TYPE)[keyof typeof TRANSACTION_TYPE];

/**
 * How each type moves an account balance (P1-BR4).
 *
 * `none` means non-cash: depreciation (P1-BR6) is an expense in the P&L that
 * never touches cash, bank or bKash, and a credit purchase (P1-BR5) raises a
 * payable without money leaving an account.
 */
export const BALANCE_EFFECT = {
  [TRANSACTION_TYPE.EXPENSE]: 'debit',
  [TRANSACTION_TYPE.INCOME]: 'credit',
  [TRANSACTION_TYPE.CAPITAL_IN]: 'credit',
  [TRANSACTION_TYPE.DRAWING]: 'debit',
  [TRANSACTION_TYPE.TRANSFER_OUT]: 'debit',
  [TRANSACTION_TYPE.TRANSFER_IN]: 'credit',
  [TRANSACTION_TYPE.PAYABLE_PAYMENT]: 'debit',
  [TRANSACTION_TYPE.RECEIVABLE_COLLECTION]: 'credit',
  [TRANSACTION_TYPE.ASSET_PURCHASE]: 'debit',
  [TRANSACTION_TYPE.DEPRECIATION]: 'none',
  [TRANSACTION_TYPE.LOAN_IN]: 'credit',
  [TRANSACTION_TYPE.LOAN_PAYMENT]: 'debit',
  [TRANSACTION_TYPE.ADVANCE_LIABILITY]: 'credit',
  [TRANSACTION_TYPE.ADVANCE_SETTLEMENT]: 'none',
  // The cash physically leaves the account, which is why this cannot be
  // `advance_settlement` with its 'none' effect.
  [TRANSACTION_TYPE.ADVANCE_REFUND]: 'debit',
} as const satisfies Record<TransactionType, 'debit' | 'credit' | 'none'>;
export type BalanceEffect = (typeof BALANCE_EFFECT)[TransactionType];

/**
 * Types that belong in the profit & loss statement.
 *
 * BR-03: partner drawings are not expenses, and capital contributions are not
 * income — both are equity movements. Getting this wrong overstates the loss
 * and would tell four partners the farm is failing when it is not.
 */
export const PNL_EXPENSE_TYPES = [
  TRANSACTION_TYPE.EXPENSE,
  TRANSACTION_TYPE.DEPRECIATION,
] as const satisfies readonly TransactionType[];

/*
 * P4-BR1 — a settled advance is earned revenue.
 *
 * It moves no cash, because the cash arrived at booking, but delivery is the
 * moment the farm has actually earned it. Leaving it out would recognise only
 * the balancing payment and understate the Eid season's revenue by every
 * deposit taken.
 */
export const PNL_INCOME_TYPES = [
  TRANSACTION_TYPE.INCOME,
  TRANSACTION_TYPE.ADVANCE_SETTLEMENT,
] as const satisfies readonly TransactionType[];

export const EQUITY_TYPES = [
  TRANSACTION_TYPE.CAPITAL_IN,
  TRANSACTION_TYPE.DRAWING,
] as const satisfies readonly TransactionType[];

/** §17.5 — BR-07 maker-checker, BR-08 soft delete. */
export const TRANSACTION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  VOIDED: 'voided',
} as const;
export type TransactionStatus = (typeof TRANSACTION_STATUS)[keyof typeof TRANSACTION_STATUS];

/** Only approved transactions count toward balances and reports (P1-BR4). */
export const COUNTS_TOWARD_BALANCE: readonly TransactionStatus[] = [TRANSACTION_STATUS.APPROVED];

/** §17.5 — BR-15. */
export const ANIMAL_STATUS = {
  ACTIVE: 'active',
  SOLD: 'sold',
  DIED: 'died',
  QURBANI_OWN_USE: 'qurbani_own_use',
} as const;
export type AnimalStatus = (typeof ANIMAL_STATUS)[keyof typeof ANIMAL_STATUS];

export const TASK_STATUS = { PENDING: 'pending', DONE: 'done', CANCELLED: 'cancelled' } as const;
export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

export const INVOICE_STATUS = { DUE: 'due', PARTIAL: 'partial', PAID: 'paid' } as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS];

export const BOOKING_STATUS = { BOOKED: 'booked', DELIVERED: 'delivered', CANCELLED: 'cancelled' } as const;
export type BookingStatus = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS];

/** §12.1 item 5 — whether a loan is still being repaid. */
export const LOAN_STATUS = { ACTIVE: 'active', CLOSED: 'closed', WRITTEN_OFF: 'written_off' } as const;
export type LoanStatus = (typeof LOAN_STATUS)[keyof typeof LOAN_STATUS];

/**
 * §1.4 — read transparency is absolute; write follows maker-checker.
 *
 * `system` is not a person. It is the identity the depreciation cron (P1-BR6)
 * posts under, so an automatic entry is never mistaken for one a partner made.
 * The account is permanently inactive, which is what makes it unable to log in
 * or refresh a token, and no route authorizes the role.
 */
export const USER_ROLE = { ADMIN: 'admin', PARTNER: 'partner', STAFF: 'staff', SYSTEM: 'system' } as const;
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

/**
 * The roles an admin may hand to a human being.
 *
 * Deliberately not `USER_ROLE`: if role validation were derived from the full
 * enum, an admin could create a second `system` account — or promote themselves
 * into one — and the audit trail would lose its ability to say "no person did
 * this".
 */
export const ASSIGNABLE_ROLES = [USER_ROLE.ADMIN, USER_ROLE.PARTNER, USER_ROLE.STAFF] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const SPECIES = { GOAT: 'goat', CATTLE: 'cattle' } as const;
export type Species = (typeof SPECIES)[keyof typeof SPECIES];

export const SEX = { MALE: 'male', FEMALE: 'female' } as const;
export type Sex = (typeof SEX)[keyof typeof SEX];

export const ANIMAL_SOURCE = { PURCHASED: 'purchased', BORN: 'born' } as const;
export type AnimalSource = (typeof ANIMAL_SOURCE)[keyof typeof ANIMAL_SOURCE];

/** BR-05 — extendable: nagad, rocket, a second bank. */
export const ACCOUNT_TYPE = { CASH: 'cash', BANK: 'bank', MFS: 'mfs' } as const;
export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE];

export const CATEGORY_KIND = { EXPENSE: 'expense', INCOME: 'income' } as const;
export type CategoryKind = (typeof CATEGORY_KIND)[keyof typeof CATEGORY_KIND];

/** BR-09 — drives the default useful life applied to an asset. */
export const ITEM_TYPE = { FEED: 'feed', MEDICINE: 'medicine' } as const;
export type ItemType = (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE];

/**
 * BR-19 — how an item is counted.
 *
 * Bhusi is bought and fed by the kilo, milk medicine by the dose. The unit is
 * fixed per item rather than per movement: a store where one purchase is in kg
 * and the next in sacks cannot be summed at all.
 */
export const ITEM_UNIT = { KG: 'kg', LITRE: 'litre', PIECE: 'piece', DOSE: 'dose' } as const;
export type ItemUnit = (typeof ITEM_UNIT)[keyof typeof ITEM_UNIT];

export const STOCK_MOVEMENT = {
  PURCHASE: 'purchase',
  CONSUME: 'consume',
  ADJUST: 'adjust',
  EXPIRED: 'expired',
} as const;
export type StockMovement = (typeof STOCK_MOVEMENT)[keyof typeof STOCK_MOVEMENT];

export const HEALTH_EVENT_TYPE = {
  VACCINATION: 'vaccination',
  DEWORMING: 'deworming',
  ILLNESS: 'illness',
  TREATMENT: 'treatment',
  VET_VISIT: 'vet_visit',
} as const;
export type HealthEventType = (typeof HEALTH_EVENT_TYPE)[keyof typeof HEALTH_EVENT_TYPE];

export const BREEDING_METHOD = { NATURAL: 'natural', AI: 'ai' } as const;
export type BreedingMethod = (typeof BREEDING_METHOD)[keyof typeof BREEDING_METHOD];

export const BREEDING_RESULT = { PENDING: 'pending', PREGNANT: 'pregnant', FAILED: 'failed' } as const;
export type BreedingResult = (typeof BREEDING_RESULT)[keyof typeof BREEDING_RESULT];

export const CUSTOMER_TYPE = { REGULAR: 'regular', WALKIN: 'walkin' } as const;
export type CustomerType = (typeof CUSTOMER_TYPE)[keyof typeof CUSTOMER_TYPE];

export const MILK_SESSION = { MORNING: 'morning', EVENING: 'evening' } as const;
export type MilkSession = (typeof MILK_SESSION)[keyof typeof MILK_SESSION];

/** BR-02 — configurable if the partners agree on a fixed split. */
export const PROFIT_SHARING_MODE = { BY_CAPITAL: 'by_capital', FIXED_RATIO: 'fixed_ratio' } as const;
export type ProfitSharingMode = (typeof PROFIT_SHARING_MODE)[keyof typeof PROFIT_SHARING_MODE];

/** P3-BR5 — both views are shown, clearly labelled. */
export const PNL_BASIS = { CASH: 'cash', ACCRUAL: 'accrual' } as const;
export type PnlBasis = (typeof PNL_BASIS)[keyof typeof PNL_BASIS];

/** BR-28 — the audit trail records intent, not just the change. */
export const AUDIT_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  APPROVE: 'approve',
  REJECT: 'reject',
  VOID: 'void',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

/** P2-BR6 — reminder kinds generated by the daily cron. */
export const REMINDER_TYPE = {
  VACCINE_DUE: 'vaccine_due',
  DELIVERY_DUE: 'delivery_due',
  STOCK_LOW: 'stock_low',
  MEDICINE_EXPIRY: 'medicine_expiry',
  RECONCILE_DUE: 'reconcile_due',
  INVOICE_DUE: 'invoice_due',
  WEIGHT_OVERDUE: 'weight_overdue',
  LOAN_INSTALLMENT_DUE: 'loan_installment_due',
} as const;
export type ReminderType = (typeof REMINDER_TYPE)[keyof typeof REMINDER_TYPE];

/** §7 — the API's standard error codes. */
export const ERROR_CODE = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  RECEIPT_REQUIRED: 'RECEIPT_REQUIRED',
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  SELF_APPROVAL_FORBIDDEN: 'SELF_APPROVAL_FORBIDDEN',
  INSUFFICIENT_STOCK: 'INSUFFICIENT_STOCK',
  ALREADY_PROCESSED: 'ALREADY_PROCESSED',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;
export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];
